// Pure helpers for the Customer Actions "Send Details" panel (SendDetailsPanel.js).

export const SEND_RANGES = [
  { id: "30d", label: "Last 30 days", days: 30 },
  { id: "90d", label: "Last 90 days", days: 90 },
  { id: "1y", label: "Last year", days: 365 },
  { id: "all", label: "All time", days: null },
  { id: "custom", label: "Custom", days: null },
];

export const SEND_STATES = [
  { id: "", label: "Any state" },
  { id: "issue_bearing", label: "Issue-bearing" },
  { id: "all_clear", label: "All clear" },
  { id: "skipped", label: "Skipped" },
];

export const PAGE_SIZE = 50;

// Query window for a range preset; nulls mean "no bound".
export function rangeToWindow(preset, customStart, customEnd, now = new Date()) {
  const range = SEND_RANGES.find((r) => r.id === preset);
  if (!range || preset === "all") return { start: null, end: null };
  if (preset === "custom") {
    return {
      start: customStart ? new Date(customStart).toISOString() : null,
      end: customEnd ? new Date(new Date(customEnd).getTime() + 24 * 60 * 60 * 1000).toISOString() : null,
    };
  }
  return { start: new Date(now.getTime() - range.days * 24 * 60 * 60 * 1000).toISOString(), end: null };
}

// How a send's engagement reads in the table.
//   kind: "skipped" | "unobservable" | "visited" | "none"
export function engagementSummary(event, windowDays = 14) {
  if (!event || !event.sent_at) return { kind: "skipped", label: "Not sent", detail: event && event.skip_reason ? event.skip_reason : "" };
  const eng = event.engagement;
  if (!eng) return { kind: "unknown", label: "n/a", detail: "Engagement not reported by this Platform build" };
  if (!eng.known_recipients) {
    return { kind: "unobservable", label: "No Hub account", detail: "None of the recipients maps to a Hub user, so visits cannot be observed" };
  }
  if (eng.visited) {
    const who = (eng.visitors || []).join(", ");
    return {
      kind: "visited",
      label: eng.landed_from_link ? "Opened from email" : "Visited run",
      detail: `${who} · ${eng.visit_count} event${eng.visit_count === 1 ? "" : "s"} within ${eng.window_days || windowDays} days` +
        (eng.landed_from_link ? " · a fresh Hub session started on the run page" : ""),
      firstVisitAt: eng.first_visit_at,
    };
  }
  return {
    kind: "none",
    label: `No visit in ${eng.window_days || windowDays}d`,
    detail: `${eng.known_recipients} recipient${eng.known_recipients === 1 ? "" : "s"} with a Hub account, none reached the run`,
  };
}

// Page-level engagement rate over the sends where it can be observed.
export function summarizeEngagement(items) {
  let sent = 0;
  let observable = 0;
  let visited = 0;
  let fromLink = 0;
  for (const e of items || []) {
    if (!e.sent_at) continue;
    sent += 1;
    if (!e.engagement || !e.engagement.known_recipients) continue;
    observable += 1;
    if (e.engagement.visited) visited += 1;
    if (e.engagement.landed_from_link) fromLink += 1;
  }
  return { sent, observable, visited, fromLink, rate: observable ? visited / observable : null };
}

export function hoursSince(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  return Number.isNaN(ms) ? null : ms / 36e5;
}

export function formatLag(hours) {
  if (hours == null) return "";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min later`;
  if (hours < 48) return `${Math.round(hours)} h later`;
  return `${Math.round(hours / 24)} d later`;
}

export function sendsToCsv(items) {
  const header = ["sent_at", "website_id", "subject", "state", "fire_path", "recipients", "engagement", "first_visit_at", "visitors", "run_id", "event_id"];
  const rows = (items || []).map((e) => {
    const summary = engagementSummary(e);
    return [
      e.sent_at || "",
      e.website_id || "",
      e.subject || "",
      e.state || "",
      e.fire_path || "",
      (e.recipients || []).join(" "),
      summary.label,
      (e.engagement && e.engagement.first_visit_at) || "",
      ((e.engagement && e.engagement.visitors) || []).join(" "),
      e.run_id || "",
      e.event_id || "",
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(",");
  });
  return header.join(",") + "\n" + rows.join("\n");
}
